import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, MapPin, ArrowRight, Compass, Search, GitCompareArrows } from "lucide-react";
import { homesRelocationHub } from "@/lib/api";

export default function RelocationHub() {
  const [cities, setCities] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => { window.scrollTo(0, 0); homesRelocationHub(48).then((d) => setCities(d.cities)).catch(() => setCities([])); }, []);

  const filtered = (cities || []).filter((c) =>
    !q || `${c.name} ${c.state || ""}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="relocation-hub">
      <section className="bg-[#0F172A] text-white">
        <div className="mx-auto max-w-[1400px] px-4 py-14">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#93B4FF]">
            <Compass className="h-3.5 w-3.5" /> Relocation Guides
          </span>
          <h1 className="mt-3 text-4xl font-black md:text-5xl">Moving to a new city?</h1>
          <p className="mt-2 max-w-2xl text-white/70">
            Place-first relocation guides — cost of living, neighborhoods, lifestyle and who each city suits — grounded in HomesPulse market and community data.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="flex max-w-md flex-1 items-center gap-2 rounded-xl bg-white px-3 py-2.5">
              <Search className="h-5 w-5 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a city…"
                className="w-full text-[#0F172A] outline-none" data-testid="relocation-search" />
            </div>
            <Link to="/homes/compare" className="flex items-center gap-1.5 rounded-xl bg-[#2563EB] px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#1D4ED8]" data-testid="relocation-compare-link">
              <GitCompareArrows className="h-4 w-4" /> Compare two cities
            </Link>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-4 py-10">
        {!cities ? (
          <div className="flex min-h-[30vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#2563EB]" /></div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="relocation-grid">
            {filtered.map((c) => (
              <Link key={c.geo_id} to={`/homes/relocation/${c.geo_id}`}
                className="group rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-[#2563EB] hover:shadow-lg"
                data-testid={`relocation-city-${c.geo_id}`}>
                <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#2563EB]">
                  <MapPin className="h-3.5 w-3.5" /> {c.state}
                </div>
                <h3 className="mt-1 text-2xl font-black text-[#0F172A]">Moving to {c.name}</h3>
                {c.median_sale_price && (
                  <div className="mt-2 text-sm text-slate-600">Median home ~ ${c.median_sale_price.toLocaleString()}</div>
                )}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {(c.known_for || []).slice(0, 3).map((k) => (
                    <span key={k} className="rounded-full bg-[#EEF2FF] px-2.5 py-1 text-xs font-medium text-[#2563EB]">{k}</span>
                  ))}
                </div>
                <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#2563EB]">
                  Read the guide <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
            {filtered.length === 0 && <p className="text-slate-500">No cities match “{q}”.</p>}
          </div>
        )}
      </div>
    </div>
  );
}
